import { portalAwareFetch } from "../utils/portalEmbedApi";

const BASE_URL = "https://workflow.grupocenze.com.br:38137/rest/";

export const PASSWORD_CHANGE_REQUIRED_MESSAGE =
  "Sua senha está expirada ou exige troca obrigatória. Solicite a troca de senha ao suporte/time responsável.";

function createPasswordChangeRequiredError() {
  const error = new Error("PASSWORD_CHANGE_REQUIRED");
  error.isPasswordChangeRequired = true;
  error.status = 403;
  error.code = "PASSWORD_CHANGE_REQUIRED";
  error.userMessage = PASSWORD_CHANGE_REQUIRED_MESSAGE;
  return error;
}

function isPasswordChangeSignal(response, apiUrl, bodyText = "") {
  const finalUrl = String(response?.url || apiUrl || "").toLowerCase();
  const body = String(bodyText || "").toLowerCase();
  const wasRedirected = Boolean(response?.redirected);
  const status = Number(response?.status || 0);

  if (
    finalUrl.includes("changepassworduserservice") ||
    finalUrl.includes("changepassword")
  ) {
    return true;
  }

  if (wasRedirected && status === 202) {
    return true;
  }

  if (
    body.includes("changepassworduserservice") ||
    body.includes("changepassword") ||
    body.includes("password_change_required") ||
    body.includes("troca de senha") ||
    body.includes("precisa efetuar a troca de senha") ||
    body.includes("se você foi redirecionado para esse endereço")
  ) {
    return true;
  }

  try {
    const parsed = JSON.parse(bodyText);
    if (
      parsed?.code === "PASSWORD_CHANGE_REQUIRED" ||
      parsed?.error === "password_change_required"
    ) {
      return true;
    }
  } catch {
    // not JSON
  }

  return false;
}

async function assertValidAuthResponse(response, apiUrl) {
  const peekText = await (typeof response.clone === "function"
    ? response.clone().text()
    : response.text()
  ).catch(() => "");

  if (isPasswordChangeSignal(response, apiUrl, peekText)) {
    throw createPasswordChangeRequiredError();
  }

  if (!response.ok) {
    let errorData = {};
    try {
      errorData = JSON.parse(peekText);
    } catch {
      errorData = {};
    }
    const error = new Error(
      errorData.error_description ||
        errorData.message ||
        errorData.error ||
        `HTTP error! status: ${response.status}`,
    );
    error.status = response.status;
    error.statusText = response.statusText;
    error.data = errorData;
    throw error;
  }

  let data;
  try {
    data = peekText ? JSON.parse(peekText) : {};
  } catch {
    throw new Error("Resposta inválida do servidor");
  }

  if (!data?.access_token) {
    if (
      isPasswordChangeSignal(response, apiUrl, peekText) ||
      response.status === 202
    ) {
      throw createPasswordChangeRequiredError();
    }
    const error = new Error("Resposta de autenticação sem access_token");
    error.status = response.status || 401;
    throw error;
  }

  return data;
}

function rethrowIfPasswordChangeRequired(error, apiUrl) {
  if (error?.isPasswordChangeRequired) {
    throw error;
  }

  const bodyText =
    typeof error?.message === "string"
      ? error.message
      : typeof error?.data === "string"
        ? error.data
        : JSON.stringify(error?.data || "");

  if (isPasswordChangeSignal(error?.response, apiUrl, bodyText)) {
    throw createPasswordChangeRequiredError();
  }
}

async function requestPasswordGrant(username, password) {
  const myHeaders = new Headers();
  myHeaders.append("username", username);
  myHeaders.append("password", password);

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    redirect: "follow",
  };

  const apiUrl = `${BASE_URL}api/oauth2/v1/token?grant_type=password`;
  try {
    const response = await portalAwareFetch(apiUrl, requestOptions);
    return assertValidAuthResponse(response, apiUrl);
  } catch (error) {
    rethrowIfPasswordChangeRequired(error, apiUrl);
    throw error;
  }
}

async function requestRefreshGrant(refreshToken) {
  const myHeaders = new Headers();
  myHeaders.append("refresh_token", refreshToken);

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    redirect: "follow",
  };

  const apiUrl = `${BASE_URL}api/oauth2/v1/token?grant_type=refresh_token`;
  const response = await portalAwareFetch(apiUrl, requestOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (
      errorData?.code === "PASSWORD_CHANGE_REQUIRED" ||
      errorData?.error === "password_change_required"
    ) {
      throw createPasswordChangeRequiredError();
    }
    const error = new Error(
      errorData.error_description || errorData.error || "Erro ao renovar token",
    );
    error.status = response.status;
    error.statusText = response.statusText;
    error.data = errorData;
    throw error;
  }

  return response.json();
}

function persistRefreshToken(tokenData, currentRefreshToken) {
  const refreshToken = tokenData?.refresh_token || currentRefreshToken || "";
  if (refreshToken) {
    localStorage.setItem("portal_refresh_token", refreshToken);
  }
}

export async function fetchToken(username, password) {
  const effectiveRefreshToken = localStorage.getItem("portal_refresh_token");
  const hasUsablePassword =
    Boolean(password) && password !== "__portal_sso__";

  try {
    let data;
    if (username && hasUsablePassword) {
      data = await requestPasswordGrant(username, password);
    } else if (effectiveRefreshToken) {
      data = await requestRefreshGrant(effectiveRefreshToken);
    } else {
      const error = new Error(
        "Não foi possível renovar autenticação: senha ausente e refresh_token indisponível.",
      );
      error.status = 401;
      throw error;
    }

    persistRefreshToken(data, effectiveRefreshToken);
    return data;
  } catch (error) {
    if (error.isPasswordChangeRequired || error.status) {
      throw error;
    }

    console.error("Error fetching token:", error);
    const networkError = new Error(
      "Erro de conexão. Verifique sua internet e tente novamente.",
    );
    networkError.status = 0;
    throw networkError;
  }
}
